<div class="ss-page">
  <div class="ss-wrap">
    <a class="back" routerLink="/account/searches">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6"></path></svg>
      Saved searches
    </a>
    <p class="eyebrow">{{ editId ? 'Edit saved search' : 'New saved search' }}</p>
    <h1 class="stmt">What should we watch for?</h1>
    <p class="lead" *ngIf="counts">Choose the kind of plate and your budget. We check every dealer, the DVLA and upcoming auctions, and email you when something new matches.</p>
    <p class="lead" *ngIf="caps && !counts">Enter a name, word or initials, and choose a style if you only want one. We alert you when a newly listed plate reads almost exactly as it.</p>

    <div class="skeleton" *ngIf="loading || !caps" aria-label="Loading saved search">
      <div class="sk-line"></div><div class="sk-line"></div><div class="sk-line short"></div>
    </div>

    <p class="note err" *ngIf="!caps && saveError" role="alert">{{ saveError }}</p>

    <div class="cols" *ngIf="!loading && caps">
      <form class="form" (submit)="$event.preventDefault(); save()" novalidate>

        <fieldset>
          <legend class="lab">Style</legend>
          <div class="chips">
            <button type="button" class="chip" *ngFor="let s of styleChoices"
              [class.on]="styleOn(s.id)" [attr.aria-pressed]="styleOn(s.id)"
              (click)="setStyle(s.id)">{{ s.label }}</button>
          </div>
          <p class="help" *ngIf="keepingStyles">This search covers {{ keptStylesLabel }} plates. Choosing a style here replaces them.</p>
        </fieldset>

        <fieldset *ngIf="choices.style === 'dateless' && offers('formats')">
          <legend class="lab lab--tight">Length and format</legend>
          <p class="sub">Pick a length, then fine-tune the formats. Letters × numbers, either way round: 1×3 covers A 123 and 123 A.</p>
          <div class="chips chips--gap">
            <button type="button" class="chip" *ngFor="let l of lengths"
              [class.on]="isLength(l)" [attr.aria-pressed]="isLength(l)"
              (click)="pickLength(l)">{{ l.characters }} characters</button>
          </div>
          <div class="fgrid">
            <button type="button" class="fcell" *ngFor="let f of formats"
              [class.on]="hasFormat(f.token)" [attr.aria-pressed]="hasFormat(f.token)"
              [attr.aria-label]="f.plainEnglish" (click)="toggleFormat(f.token)">{{ f.display }}</button>
          </div>
        </fieldset>

        <fieldset *ngIf="(choices.style === 'prefix' || choices.style === 'suffix') && offers('characters')">
          <legend class="lab lab--tight">Length</legend>
          <p class="sub">{{ choices.style === 'prefix' ? 'Prefix plates run from A1 ABC (5 characters) to A123 ABC (7).' : 'Suffix plates run from ABC 1A (5 characters) to ABC 123A (7).' }} Leave all off for any length.</p>
          <div class="chips">
            <button type="button" class="chip" *ngFor="let n of psLengths"
              [class.on]="hasChars(n)" [attr.aria-pressed]="hasChars(n)"
              (click)="toggleChars(n)">{{ n }} characters</button>
          </div>
        </fieldset>

        <div *ngIf="choices.style === 'current' && offers('maxYear')">
          <label class="lab" for="ss-year">Plate year</label>
          <select id="ss-year" class="field field--year" [(ngModel)]="choices.maxYear" name="maxYear" (ngModelChange)="changed()">
            <option value="">Any year</option>
            <option *ngFor="let y of years" [value]="'' + y">{{ y }} or earlier</option>
          </select>
          <p class="help">A plate can't make a vehicle look newer than it is. Choose your vehicle's year to see only plates it can carry. A September plate, such as 69, only fits vehicles first registered from that September.</p>
        </div>

        <div>
          <label class="lab" for="ss-term">Reads as{{ termRequired ? '' : ' (optional)' }}</label>
          <input id="ss-term" class="field field--term" type="text" placeholder="HUGH" autocomplete="off"
            [(ngModel)]="choices.term" name="term" (ngModelChange)="changed()">
          <p class="help">A name, word or initials. Only plates that read almost exactly as it.</p>
        </div>

        <fieldset *ngIf="offers('priceMin')">
          <legend class="lab lab--tight">Budget</legend>
          <div class="budget">
            <label class="money"><span aria-hidden="true">£</span><span class="vh">Minimum, pounds</span>
              <input type="text" inputmode="numeric" placeholder="No minimum" [(ngModel)]="choices.minPounds" name="min" (ngModelChange)="changed()">
            </label>
            <span class="to">to</span>
            <label class="money"><span aria-hidden="true">£</span><span class="vh">Maximum, pounds</span>
              <input type="text" inputmode="numeric" placeholder="No maximum" [(ngModel)]="choices.maxPounds" name="max" (ngModelChange)="changed()">
            </label>
          </div>
          <p class="help">All-in: what you'd pay, including VAT and the £80 transfer fee.</p>
        </fieldset>

        <div class="toggle-row" *ngIf="offersAuctions">
          <div>
            <p class="tr-t">Include auction lots</p>
            <p class="tr-d">Plates coming up at DVLA and other auctions, matched on their guide or current bid.</p>
          </div>
          <button type="button" class="switch" role="switch" [class.on]="choices.includeAuctions"
            [attr.aria-checked]="choices.includeAuctions" aria-label="Include auction lots"
            (click)="toggleAuctions()"><span class="knob"></span></button>
        </div>

        <fieldset *ngIf="frequencyChoices.length > 1">
          <legend class="lab">Email me</legend>
          <div class="freqs">
            <button type="button" class="freq" *ngFor="let q of frequencyChoices"
              [class.on]="choices.frequency === q.id" [attr.aria-pressed]="choices.frequency === q.id"
              (click)="setFrequency(q.id)">
              <span class="fq-t">{{ q.label }}</span>
              <span class="fq-d">{{ q.help }}</span>
            </button>
          </div>
          <p class="help warn" *ngIf="instantBlocked" role="alert">Too many new plates for as-it-happens emails. Choose daily or weekly.</p>
          <p class="help">One email at most per day for all your searches. Every email has a one-click "send less often".</p>
        </fieldset>
      </form>

      <aside class="panel" aria-live="polite">
        <p class="lab">Your search</p>
        <p class="sum" [class.dim]="estimating">{{ summary }}</p>
        <div class="stats" *ngIf="counts">
          <div class="stat"><p class="sl">For sale now</p><p class="sv">{{ forSale }}</p></div>
          <div class="stat"><p class="sl">New in a typical week</p><p class="sv">{{ perWeek }}</p></div>
        </div>
        <p class="note err" *ngIf="estimateError" role="alert">{{ estimateError }}</p>
        <p class="note" *ngIf="!estimateError">{{ counts ? estimateNote : alertNote }}</p>
        <button type="button" class="av2-btn av2-btn--green save" [disabled]="saving" (click)="save()">{{ saving ? 'Saving…' : (editId ? 'Save changes' : 'Save search') }}</button>
        <p class="note err" *ngIf="saveError" role="alert">{{ saveError }}</p>
        <div class="panel-links">
          <button type="button" class="linkish" *ngIf="editId && counts" (click)="seeMatching()">See the matching plates</button>
          <a class="linkish quiet" routerLink="/account/searches">Cancel</a>
        </div>
      </aside>
    </div>
  </div>
</div>
